// @catalog F.030
// DecisionCard — Hero del drawer: 4 columnas (Inventario · Reorder ·
// Sugerido · Input+Aplicar). Reusa exactamente la lógica de PATCH del
// dialog viejo (`/forecasting/product-mapping/:id/purchase-cant-user`).
//
// "Sugerido" = fallback `Math.max(0, reorderPoint - totalInventory)` porque
// el backend NO expone forecast.next3Months todavía. Marcado con TODO.

import { useMemo, useState } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import type { ProductMapping } from "../productDetailTypes";
import { FORECAST_METHOD_DESC, fmtNum } from "./helpers";
import { PackSizeEditor } from "./PackSizeEditor";

interface DecisionCardProps {
  mapping: ProductMapping;
  totalInventory: number;
  purchaseQty: string;
  setPurchaseQty: (v: string) => void;
  saving: boolean;
  saved: boolean;
  onApply: () => void;
  /** Vuelve al auto detectado (backend recibe null). Optional para que la
   * unidad de prueba pueda omitirlo si el botón Restablecer no se necesita. */
  onReset?: () => void;
}

type InventoryStatus = "alert" | "warn" | "ok";

function deriveInventoryStatus(
  totalInventory: number,
  safety: number | null,
  reorder: number | null,
): { status: InventoryStatus; label: string } {
  if (safety != null && totalInventory < safety) {
    return { status: "alert", label: "Bajo safety stock" };
  }
  if (reorder != null && totalInventory < reorder) {
    return { status: "warn", label: "Bajo punto reorden" };
  }
  return { status: "ok", label: "OK" };
}

export function DecisionCard({
  mapping,
  totalInventory,
  purchaseQty,
  setPurchaseQty,
  saving,
  saved,
  onApply,
  onReset,
}: DecisionCardProps) {
  // Estado derivado
  const inventoryStatus = useMemo(
    () =>
      deriveInventoryStatus(
        totalInventory,
        mapping.safetyStock,
        mapping.reorderPoint,
      ),
    [totalInventory, mapping.safetyStock, mapping.reorderPoint],
  );

  // Sugerido (fallback) — TODO(backend): integrar forecast.next3Months del
  // pipeline canónico cuando el endpoint exista. Mientras tanto:
  //   sugerido = max(0, reorderPoint - inventory)
  const suggested = useMemo(() => {
    if (mapping.reorderPoint == null) return 0;
    return Math.max(0, mapping.reorderPoint - totalInventory);
  }, [mapping.reorderPoint, totalInventory]);

  // Valor efectivo actual: override usuario > auto detectado > 1.
  // El pack size mínimo válido es 1 (nunca cero).
  const effectiveCant = useMemo(
    () => Math.max(1, mapping.purchaseCantUser ?? mapping.purchaseCant ?? 1),
    [mapping.purchaseCantUser, mapping.purchaseCant],
  );
  const hasUserOverride = mapping.purchaseCantUser != null;

  const [methodTipOpen, setMethodTipOpen] = useState(false);

  // Color del valor de inventario según status
  const inventoryValueColor =
    inventoryStatus.status === "alert"
      ? "var(--neg)"
      : inventoryStatus.status === "warn"
        ? "var(--warn)"
        : "var(--ink-1)";

  return (
    <>
      <Box
        sx={{
          mx: 2.5,
          mt: 2,
          mb: 1.5,
          bgcolor: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 1.5,
          p: 2,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr minmax(120px, 150px)",
          gap: 2,
          alignItems: "center",
        }}
      >
        {/* Inventario */}
        <Stack spacing={0.25}>
          <Typography
            sx={{
              fontSize: 11,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 500,
            }}
          >
            Inventario
          </Typography>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
              color: inventoryValueColor,
              lineHeight: 1.15,
            }}
          >
            {fmtNum(totalInventory)}
          </Typography>
          <Typography
            sx={{
              fontSize: 11.5,
              color: "var(--ink-3)",
            }}
          >
            {inventoryStatus.label}
          </Typography>
        </Stack>

        {/* Punto reorden */}
        <Stack spacing={0.25}>
          <Typography
            sx={{
              fontSize: 11,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 500,
            }}
          >
            Punto reorden
          </Typography>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
              color: "var(--ink-1)",
              lineHeight: 1.15,
            }}
          >
            {fmtNum(mapping.reorderPoint)}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "var(--ink-3)" }}>
            Safety {fmtNum(mapping.safetyStock)}
          </Typography>
        </Stack>

        {/* Sugerido */}
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              sx={{
                fontSize: 11,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 500,
              }}
            >
              Sugerido
            </Typography>
            <Tooltip
              title="Cuántas piezas pedir para reponer. Fallback actual: punto de reorden menos inventario; al integrarse el forecast multi-mes mostrará la suma del próximo trimestre."
              arrow
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 12, color: "var(--ink-4)", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
              color: "var(--accent)",
              lineHeight: 1.15,
            }}
          >
            {fmtNum(suggested)}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "var(--ink-3)" }}>
            Auto: {mapping.purchaseCant != null ? mapping.purchaseCant : "—"}
          </Typography>
        </Stack>

        {/* Por paquete — readonly por default; click lápiz entra a edit.
            Misma jerarquía vertical que los otros 3 stats (label + valor +
            sub-text). El editor se monta dentro del slot del valor. */}
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              sx={{
                fontSize: 11,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 500,
              }}
            >
              Por paquete
            </Typography>
            <Tooltip
              title="Cantidad por paquete usada para redondear las cantidades sugeridas. El sistema multiplica este valor para cubrir lo que el forecast pide."
              arrow
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 12, color: "var(--ink-4)", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
          <PackSizeEditor
            effectiveCant={effectiveCant}
            hasUserOverride={hasUserOverride}
            purchaseQty={purchaseQty}
            setPurchaseQty={setPurchaseQty}
            saving={saving}
            saved={saved}
            onApply={onApply}
            {...(onReset ? { onReset } : {})}
          />
          <Typography
            sx={{
              fontSize: 11.5,
              color: hasUserOverride ? "var(--accent)" : "var(--ink-3)",
              fontWeight: hasUserOverride ? 500 : 400,
            }}
          >
            {hasUserOverride
              ? `Personalizado (auto: ${mapping.purchaseCant ?? "—"})`
              : "Auto del producto"}
          </Typography>
        </Stack>
      </Box>

      {/* Context bar */}
      <Stack
        direction="row"
        spacing={1.75}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          fontSize: 12,
          color: "var(--ink-3)",
          px: 2.5,
          pb: 1.5,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          <TrendingUpIcon sx={{ fontSize: 13, color: "var(--accent)" }} />
          <span>Método</span>
          <Box
            component="strong"
            sx={{ color: "var(--ink-1)", fontWeight: 600 }}
          >
            {mapping.matchingMethod || "—"}
          </Box>
          <Tooltip
            open={methodTipOpen}
            onOpen={() => setMethodTipOpen(true)}
            onClose={() => setMethodTipOpen(false)}
            title={
              FORECAST_METHOD_DESC[mapping.matchingMethod] ||
              "Método de emparejamiento entre Odoo 16 y Odoo 18."
            }
            arrow
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 12, color: "var(--ink-4)", cursor: "help" }}
            />
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <span>Confianza</span>
          <Box
            component="strong"
            sx={{
              color: "var(--ink-1)",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {mapping.matchingConfidence}%
          </Box>
        </Stack>
        {/* Item "Pack" eliminado del context bar — ya vive como 4ta columna
            del Decision Card con label "Por paquete" + sub-text de estado. */}
      </Stack>
    </>
  );
}
